// Copyright (C) 2021 Bystronic Laser AG

import { Shift } from '@/models/shift';
import { shiftsService } from '@/services/shifts.service';
import { Device } from '@/models/device';
import { isEmpty } from '@/utils/misc';

/**
 * Returns a list of shifts applied implicitly or explicitly according to the
 * selected shifts and devices on their respective filters.
 *
 * It takes into account that when no devices have been selected in the filter,
 * all of them are selected implicitly, and that no shifts selected doesn't
 * imply all of them are selected.
 *
 * It also excludes selected shifts that belong to non-selected devices.
 *
 * @param selectedShiftIds List of shift ids selected in the filter.
 * @param selectedDeviceStringIds List of device string ids selected in the
 *   filter.
 * @param availableDevices List of all available devices in the filter.
 */
export function getActiveShifts(
  selectedShiftIds: number[],
  selectedDeviceStringIds: string[],
  availableDevices: Device[],
): Shift[] {
  const deviceIds = availableDevices.map((device) => device.deviceId);

  return isEmpty(selectedShiftIds)
    ? []
    : getShiftsFromActiveDevices(selectedDeviceStringIds, deviceIds).filter(
        (shift) => selectedShiftIds.includes(shift.id),
      );
}

function getShiftsFromActiveDevices(
  selectedDeviceStringIds: string[],
  availableDeviceStringIds: string[],
): Shift[] {
  return shiftsService.store.getAllByDeviceIds(
    getActiveDeviceIds(selectedDeviceStringIds, availableDeviceStringIds),
  );
}

function getActiveDeviceIds(
  selectedDeviceStringIds: string[],
  availableDeviceStringIds: string[],
): string[] {
  return isEmpty(selectedDeviceStringIds)
    ? availableDeviceStringIds
    : selectedDeviceStringIds;
}
